<template>
  <div class="quick-action-after">
    <Alert
      v-if="hasMissingFields && isEditMode"
      class="quick-action-after__banner"
      data-testid="quick-action-after-banner"
      :vuetifyProps="{
        color: 'yellow',
        icon: 'mdi-information',
      }"
      :text="missingFieldsText"
    />
    <PhaseSection
      data-testid="quick-action-after-phase"
      :title="$t('quick_action.phases.after')"
      :description="$t('quick_action.after.add_a_description')"
      :isEditMode="isEditMode"
      :hasNextButton="hasNextButton"
      :hasBackButton="hasBackButton"
      @next="$emit('next')"
      @back="$emit('back')"
    >
      <MediaContainer
        class="mt-6"
        data-private
        data-testid="quick-action-solution-media"
        id="solutionPhoto"
        key="solution-image"
        slot="left"
        :images="solutionMedia"
        :isEditMode="isEditMode"
        :readOnly="!isEditMode || isSaving"
        @change="handleSolutionMediaChange"
      />
      <TranslationLoading slot="right" v-if="isTranslating" />
      <div slot="right" v-else>
        <p class="d-flex justify-end caption blue-grey--text mb-6">
          * {{ $t('common.required_fields') }}
        </p>
        <TextQuestion
          v-model="solution"
          data-private
          data-testid="quick-action-solution"
          rows="4"
          :maxLength="$constants.quickAction.solutionMaxLength"
          :tourId="'quickAction-textField-whatDidYouDo'"
          :error="markRequiredFields && !solution"
          :question="`${$t('quick_action.after.what_did_you_do')}*`"
          :placeholder="$t('quick_action.after.what_did_you_do_placeholder')"
          :is-edit-mode="isEditMode"
          @focusOut="trackEvent('addSolution')"
        />
        <div
          class="d-flex flex-column align-start"
          :class="{ 'mt-0': isEditMode, 'mt-6': !isEditMode }"
        >
          <FieldLabel
            v-if="!isEditMode"
            :text="$t('quick_action.after.suggested_tags')"
          />

          <div style="width: 100%">
            <TagsSelector
              v-if="isEditMode"
              :label="$t('quick_action.after.suggested_tags')"
              :siteId="siteId"
              :value="selectedTags"
              @change="selectTag"
            />
            <template
              v-else-if="rev.afterPhase.suggestedTags.length && !isEditMode"
            >
              <v-chip
                v-for="tag in selectedTagNames"
                :key="tag"
                class="mt-2 me-1"
                outlined
                small
                >{{ tag }}</v-chip
              >
            </template>
            <v-chip v-else disabled small class="mt-2">{{
              $t('common.in_progress_status')
            }}</v-chip>
          </div>
        </div>

        <div class="mt-6" style="width: 100%">
          <FieldLabel
            v-if="!isEditMode"
            :text="$t('quick_action.after.who_solved_this_problem')"
          />
          <div>
            <template v-if="isEditMode">
              <AssigneesSelector
                localSearch
                :buttonText="$t('quick_action.after.select_team_member')"
                :availableLocalAccounts="accounts"
                :label="$t('quick_action.after.who_solved_this_problem')"
                :selectedAccountIds="selectedMember ? [selectedMember] : []"
                @change="selectTeamMember"
              />
            </template>
            <ChipAvatar
              v-else-if="showWhoSolvedChip"
              class="mt-2 me-1"
              small
              :account="completedBy"
            />

            <v-chip v-else disabled small class="mt-2">{{
              $t('common.in_progress_status')
            }}</v-chip>
          </div>
        </div>
        <Button
          v-if="!isRevCompleted"
          color="primary"
          class="d-flex align-self-end align-center mt-2"
          data-testid="after_complete_rev"
          data-tourid="quick-action-button-complete-rev"
          :class="{ 'mt-6': !isEditMode }"
          :text="$t('rev.complete_rev')"
          :disabled="disableCompleteButton"
          @click="handleCompleteRev"
        />
      </div>
    </PhaseSection>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapMutations, mapGetters } from 'vuex'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import mixin from '@/mixins/mixin.vue'

import Alert from '@/components/Alert'
import FieldLabel from '@/components/Core/FieldLabel'
import PhaseSection from '@/components/Rev/PhaseSection/PhaseSection.vue'
import TextQuestion from '@/components/Rev/TextQuestion.vue'
import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import TranslationLoading from '@/components/Rev/TranslationLoading.vue'
import Button from '@/components/UI/Button'
import TagsSelector from '@/components/TagsSelector/TagsSelector.vue'
import AssigneesSelector from '@/components/AssigneesSelector/AssigneesSelector.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'

export default {
  name: 'After',

  props: {
    isEditMode: {
      type: Boolean,
      default: false,
    },

    rev: {
      type: Object,
      required: true,
    },

    isSaving: Boolean,
    isTranslating: Boolean,
    hasNextButton: Boolean,
    hasBackButton: Boolean,
    markRequiredFields: Boolean,
    canComplete: Boolean,
  },

  components: {
    Alert,
    PhaseSection,
    TextQuestion,
    FieldLabel,
    MediaContainer,
    TranslationLoading,
    Button,
    TagsSelector,
    AssigneesSelector,
    ChipAvatar,
  },

  mixins: [trackerMixin, mixin],

  data() {
    return {
      selectedTags: this.rev?.afterPhase?.suggestedTags || [],
      selectedMember: this.rev?.completedBy?._id ?? null,
      accounts: [],
      cachedAccounts: [],
    }
  },

  mounted() {
    this.setAccounts()
  },

  watch: {
    'rev.team': {
      immediate: true,
      deep: true,
      handler() {
        this.setAccounts()
      },
    },
  },

  computed: {
    ...mapGetters({
      tags: 'organizationTags',
    }),

    disableCompleteButton() {
      return !this.canComplete
    },

    siteId() {
      return this.rev?.location?.site?._id ?? this.rev?.location?.site
    },

    solution: {
      get() {
        return _.get(this.rev, 'afterPhase.solution', '')
      },

      set(solution) {
        this.$emit('revChange')
        this.updateSolution(solution)
      },
    },

    solutionMedia() {
      return this.rev?.afterPhase?.media ?? []
    },

    missingFieldsText() {
      return this.$t('rev.missing_fields_alert')
    },

    hasMissingFields() {
      const { beforePhase = {} } = this.rev
      const { situation } = beforePhase

      return _.isEmpty(situation)
    },

    selectedTagNames() {
      const tags = this.tags.filter(attribute => {
        return this.selectedTags.some(element => {
          return element === attribute.id
        })
      })
      return tags.map(element => element.name)
    },

    showWhoSolvedChip() {
      return !_.isEmpty(this.rev.completedBy) && !this.isEditMode
    },

    completedBy() {
      return {
        ...this.rev?.completedBy,
        displayname: this.rev?.completedBy?.displayName ?? '',
        familyname: this.rev?.completedBy?.familyName ?? '',
      }
    },
    isRevCompleted() {
      return this.rev.status === this.$constants.revStatus.completed
    },
  },

  methods: {
    ...mapMutations({
      updateSolution: 'quickAction/updateSolution',
      updateSolutionMedia: 'quickAction/updateSolutionMedia',
      updateSuggestedTags: 'quickAction/updateSuggestedTags',
      updateCompletedAuthor: 'quickAction/updateCompletedAuthor',
    }),

    handleSolutionMediaChange(media) {
      this.trackEvent('solutionMediaPhoto')
      this.$emit('revChange')
      this.updateSolutionMedia(media)
    },

    trackEvent: function(changedField) {
      this.trackUpdate(this.rev, changedField)
    },

    selectTag(value) {
      this.selectedTags = value.tags
      this.updateSuggestedTags(value.tags)
    },

    handleCompleteRev() {
      this.$emit('completeRev')
    },

    selectTeamMember(member) {
      this.$emit('revChange')
      const completedAuthor = this.getMember(member?.[0] ?? null)
      this.updateCompletedAuthor(completedAuthor)
      this.selectedMember = completedAuthor?._id
    },

    getMember(id) {
      if (!id) return null
      return this.accounts.find(account => account._id === id)
    },

    searchTeamMembers(terms) {
      if (terms) {
        const accounts = this.cachedAccounts.filter(member =>
          (member?.name?.toLowerCase?.() ?? '').includes(terms?.toLowerCase())
        )
        this.accounts = accounts
        return this.accounts
      }
      return this.setAccounts()
    },

    setAccounts() {
      const accounts = _.compact([
        {
          ...this.rev.team.owner,
          displayname: this.rev?.team?.owner?.displayName ?? '',
          familyname: this.rev?.team?.owner?.familyName ?? '',
          position: this.$t('common.roles.author'),
        },
        this.rev.team.coach?.id
          ? {
              ...this.rev.team.coach,
              displayname: this.rev?.team?.coach?.displayName ?? '',
              familyname: this.rev?.team?.coach?.familyName ?? '',
              position: this.$t('common.roles.coach_one'),
            }
          : null,
        ...this.rev.team.collaborators.map(collaborator => {
          return {
            ...collaborator,
            displayname: collaborator.displayName,
            familyname: collaborator.familyName,
            position: this.$t('common.roles.collaborator_one'),
          }
        }),
      ])

      this.accounts = accounts
      this.cachedAccounts = accounts
    },
  },
}
</script>

<style lang="scss" scoped>
.quick-action-after {
  display: flex;
  flex-direction: column;

  &__step-status {
    margin-bottom: 20px;
    padding: 0;
  }

  &__banner {
    margin-bottom: 24px;
  }

  &__complete-rev {
    align-self: flex-end;
    display: flex;
    align-items: center;
  }

  &__button {
    align-self: flex-end;
    display: flex;
    align-items: center;
  }

  &__tags-title {
    color: $color-blue-grey-darken1;
    font-size: 12px;
  }

  &__tags-selector {
    margin-bottom: 10px;
  }
}
</style>
